/**
 * `<ForgeTerminal>` — the dark CRT terminal VIRGIL speaks through in FORGE's panel: char-by-char
 * reveal, sequential transcript, blinking caret. One-directional by design — VIRGIL types out a
 * build readout; there's no inbound operator input here.
 *
 * **Presentation only — owns no model.** `ForgeKey.tsx` maps the guide's items into a
 * `TermLine[]` (VIRGIL's copy, the SHOW ▸ and research ↗ affordances) and hands it here; this file
 * just reveals and styles it. The voice lives in `virgilCopy.ts`, the items in `forgeGuide.ts`.
 *
 * **Reveal contract.** `animate` true → lines reveal SEQUENTIALLY, the active line typing on
 * character-by-character and its affordances appearing once it lands, the next line held until then.
 * `animate` false (a reopen, or `prefers-reduced-motion`) → the whole transcript paints instantly
 * with every affordance live. The decision to animate is made once at mount by the caller, so a
 * roster tick mid-session never re-types the transcript.
 */
import { faUpRightFromSquare } from "@fortawesome/free-solid-svg-icons";
import { Icon } from "../../lib/Icon";
import { useEffect, useRef, useState } from "react";
import "./forge-terminal.css";

const TYPE_MS = 16; // per-character; brisk — reads as a fast terminal, not a slideshow.

/** A SHOW ▸ / research ↗ affordance carried by a task line. */
export type TermAction = { label: string; title: string; onClick: () => void };

/**
 * One transcript line. `virgil`/`signoff`/`system` are narration, `section` a section's label;
 * `task` is an actionable item.
 */
export type TermLine = {
  id: string;
  kind: "virgil" | "task" | "signoff" | "system" | "section";
  text: string;
  /** Severity styling for a `task` line (an over-budget/invalid signal renders amber). */
  warn?: boolean;
  /** SHOW ▸: the task row itself, which jumps to the field that fixes it. */
  action?: TermAction;
  /** The research-↗ affordance — deep-links the record into COMPENDIUM. */
  research?: TermAction;
};

/** Char-by-char reveal of `text`; calls `onDone` once fully typed. Caret blinks while typing. */
function TypeOn({ text, onDone }: { text: string; onDone: () => void }) {
  const [shown, setShown] = useState(0);
  const doneRef = useRef(onDone);
  doneRef.current = onDone;

  useEffect(() => {
    if (shown >= text.length) {
      doneRef.current();
      return;
    }
    const t = setTimeout(() => setShown((n) => n + 1), TYPE_MS);
    return () => clearTimeout(t);
  }, [shown, text.length]);

  const typing = shown < text.length;
  return <span className={typing ? "forge-term-caret" : undefined}>{text.slice(0, shown)}</span>;
}

/** The research-↗ link — bare terminal type, muted. */
function ResearchLink({ action }: { action: TermAction }) {
  return (
    <button type="button" className="forge-term-link is-research" title={action.title} onClick={action.onClick}>
      {action.label} <Icon icon={faUpRightFromSquare} className="forge-term-arrow" />
    </button>
  );
}

/** A task's severity glyph: ▲ room left, ⚠ invalid. */
const taskGlyph = (line: TermLine) => <span className="forge-term-glyph" aria-hidden="true">{line.warn ? "⚠" : "▲"}</span>;

/** A single rendered line. A settled task is its SHOW ▸ button, with its research ↗ under it. */
function LineBody({ line }: { line: TermLine }) {
  if (line.kind === "task") {
    const cls = `forge-term-task${line.warn ? " is-warn" : ""}`;
    return (
      <>
        {line.action ? (
          <button type="button" className={cls} title={line.action.title} onClick={line.action.onClick}>
            {taskGlyph(line)}
            <span className="forge-term-signal">{line.text}</span>
            <span className="forge-term-show">{line.action.label}</span>
          </button>
        ) : (
          <div className={cls}>
            {taskGlyph(line)}
            <span className="forge-term-signal">{line.text}</span>
          </div>
        )}
        {line.research && <ResearchLink action={line.research} />}
      </>
    );
  }
  if (line.kind === "section") return <div className="forge-term-section">{line.text}</div>;
  return (
    <div className={`forge-term-line${line.kind === "signoff" ? " forge-term-signoff" : ""}`}>
      <span className="forge-term-glyph">{line.kind === "system" ? "::" : ">"}</span>
      <span className="forge-term-text">{line.text}</span>
    </div>
  );
}

/**
 * The terminal feed. When `animate`, reveals lines one at a time (the active line types on and
 * bumps the reveal count when done); otherwise paints the whole transcript at once. A resting
 * `>` prompt caret blinks under the last revealed line.
 */
export function ForgeTerminal({ lines, animate }: { lines: readonly TermLine[]; animate: boolean }) {
  // `revealed` = count of fully-committed lines; the line at index `revealed` is the active
  // (typing) one. Non-animated terminals reveal everything immediately.
  const [revealed, setRevealed] = useState(animate ? 0 : lines.length);

  // Clamp if the transcript shrinks (a roster tick removing a now-finished section).
  useEffect(() => {
    if (revealed > lines.length) setRevealed(lines.length);
  }, [revealed, lines.length]);

  // A `task` line with no action text still needs to "type"; but tasks carry short signal text
  // and we want their affordances gated on the line landing, so every line animates via TypeOn.
  const active = animate ? lines[revealed] : undefined;
  const visible = animate ? lines.slice(0, revealed + 1) : lines;

  return (
    <div className="forge-term-root">
      <div className="forge-term-feed">
        {visible.map((line) => {
          const isActive = animate && line === active;
          return (
            <div key={line.id}>
              {isActive ? (
                <TypeOnLine line={line} onDone={() => setRevealed((n) => n + 1)} />
              ) : (
                <LineBody line={line} />
              )}
            </div>
          );
        })}
        {/* Resting prompt — the terminal is "waiting" once the transcript settles. */}
        <div className="forge-term-prompt">
          <span className="forge-term-prompt-caret" aria-hidden />
        </div>
      </div>
    </div>
  );
}

/** The active line: types its text on, then renders the settled body (affordances live). */
function TypeOnLine({ line, onDone }: { line: TermLine; onDone: () => void }) {
  const [typed, setTyped] = useState(false);
  if (typed) return <LineBody line={line} />;
  const typeOn = (
    <TypeOn
      text={line.text}
      onDone={() => {
        setTyped(true);
        onDone();
      }}
    />
  );
  // Render the line's frame with the text typing on; once done, swap to the settled body.
  if (line.kind === "task") {
    return (
      <div className={`forge-term-task${line.warn ? " is-warn" : ""}`}>
        {taskGlyph(line)}
        <span className="forge-term-signal">{typeOn}</span>
      </div>
    );
  }
  if (line.kind === "section") return <div className="forge-term-section">{typeOn}</div>;
  return (
    <div className={`forge-term-line${line.kind === "signoff" ? " forge-term-signoff" : ""}`}>
      <span className="forge-term-glyph">{line.kind === "system" ? "::" : ">"}</span>
      <span className="forge-term-text">{typeOn}</span>
    </div>
  );
}
